import React, { useState, useRef, useEffect } from 'react';
import { FaChevronDown } from 'react-icons/fa';

const ClickableDropdown = ({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  className = "",
  disabled = false,
  searchable = true,
  clearable = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || '');
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setSelectedValue(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    setSelectedValue(optionValue);
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  const openDropdown = () => {
    if (disabled) return;
    if (searchable) {
      setSearchTerm(displayValue || "");
      setIsOpen(true);
      return;
    }
    setIsOpen((prev) => !prev);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    setSelectedValue("");
    onChange("");
    setIsOpen(false);
    setSearchTerm("");
  };

  const getOptionLabel = (option) => {
    if (typeof option?.label === 'string') return option.label;
    if (typeof option === 'string') return option;
    return String(option?.label ?? option?.value ?? option?.name ?? '');
  };

  // Safely get display value - ensure it's always a string
  const displayValue = (() => {
    const found = options.find((opt) => opt?.value === selectedValue);
    if (found) return getOptionLabel(found);
    if (selectedValue && typeof selectedValue === 'string') return selectedValue;
    return '';
  })();

  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
      if (displayValue) {
        inputRef.current.select();
      }
    }
  }, [isOpen, searchable, displayValue]);

  const filteredOptions = !searchable
    ? options
    : options.filter((option) => {
        const label = getOptionLabel(option);
        return label.toLowerCase().includes(searchTerm.toLowerCase());
      });

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={openDropdown}
        className={`w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white text-left flex items-center justify-between ${className} ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        {searchable ? (
          <input
            ref={inputRef}
            type="text"
            value={isOpen ? searchTerm : displayValue}
            onFocus={() => {
              openDropdown();
            }}
            onChange={(e) => {
              if (!isOpen) setIsOpen(true);
              setSearchTerm(e.target.value);
            }}
            placeholder={isOpen || !displayValue ? placeholder : ''}
            className={`w-full bg-transparent border-0 p-0 text-left focus:outline-none ${
              displayValue ? 'text-gray-900' : 'text-gray-500'
            } ${disabled ? 'cursor-not-allowed' : ''}`}
            disabled={disabled}
          />
        ) : (
          <span className={`truncate ${selectedValue ? 'text-gray-900' : 'text-gray-500'}`}>
            {displayValue || placeholder}
          </span>
        )}
        <div
          className="ml-2 flex items-center gap-2 shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            setIsOpen((prev) => {
              const next = !prev;
              if (next) {
                setSearchTerm(displayValue || '');
              } else {
                setSearchTerm('');
              }
              return next;
            });
          }}
        >
          {clearable && selectedValue ? (
            <span
              onClick={handleClear}
              className="text-gray-400 hover:text-red-600 text-xs leading-none"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleClear(e);
              }}
            >
              x
            </span>
          ) : null}
          <FaChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">No options available</div>
          ) : (
            filteredOptions.map((option, index) => {
              const label = getOptionLabel(option);
              return (
                <button
                  key={option?.value || index}
                  type="button"
                  onClick={() => handleSelect(option?.value)}
                  className="w-full px-3 py-2 text-xs text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                >
                  {label}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ClickableDropdown;

